import TurndownService from "turndown"

type GridTableCell = string[]

const BLOCK_RICH_TABLE_CELL_SELECTOR = [
  "pre",
  "ul",
  "ol",
  "blockquote",
  "table",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  ".math-block",
  "[data-type='mathBlock']",
  "[data-math-block]",
].join(",")

function encodeMarkdownTableText(text: string): string {
  return text
    .replace(/\\\\/g, "\\")
    .replace(/[\\|]/g, (char) => (char === "\\" ? "&#92;" : "&#124;"))
}

function encodeCodeText(text: string): string {
  return text.replace(/[&<>\\|]/g, (char) => {
    switch (char) {
      case "&":
        return "&amp;"
      case "<":
        return "&lt;"
      case ">":
        return "&gt;"
      case "\\":
        return "&#92;"
      case "|":
        return "&#124;"
      default:
        return char
    }
  })
}

function encodeTableCellMarkdown(markdown: string): string {
  const codeSpanPattern = /(`+)([\s\S]*?)\1/g
  let result = ""
  let lastIndex = 0

  for (const match of markdown.matchAll(codeSpanPattern)) {
    const matchIndex = match.index
    result += encodeMarkdownTableText(markdown.slice(lastIndex, matchIndex))
    result += `<code>${encodeCodeText(match[2])}</code>`
    lastIndex = matchIndex + match[0].length
  }

  result += encodeMarkdownTableText(markdown.slice(lastIndex))
  return result
}

function compactTableCellMarkdown(markdown: string): string {
  return encodeTableCellMarkdown(
    markdown
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .join("<br>")
  )
}

function normalizeGridCellMarkdown(markdown: string): GridTableCell {
  const normalized = markdown.replace(/\r\n?/g, "\n").replace(/\t/g, "    ").trim()

  if (normalized.length === 0) {
    return [" "]
  }

  return normalized.split("\n").map((line) => line.replace(/\s+$/g, ""))
}

function getTableRows(table: HTMLElement): HTMLTableRowElement[] {
  return Array.from((table as HTMLTableElement).rows)
}

function hasBlockRichTableCell(table: HTMLElement): boolean {
  return Array.from(table.querySelectorAll("td, th")).some((cell) =>
    cell.querySelector(BLOCK_RICH_TABLE_CELL_SELECTOR)
  )
}

function getCompactRowCells(row: HTMLTableRowElement, service: TurndownService): string[] {
  const cells: string[] = []

  for (const cell of Array.from(row.cells)) {
    const markdown = compactTableCellMarkdown(service.turndown(cell.innerHTML).trim())
    const colSpan = Math.max(Number(cell.getAttribute("colspan") ?? 1) || 1, 1)
    cells.push(markdown || " ")

    for (let i = 1; i < colSpan; i++) {
      cells.push(" ")
    }
  }

  return cells
}

function getGridRowCells(row: HTMLTableRowElement, service: TurndownService): GridTableCell[] {
  const cells: GridTableCell[] = []

  for (const cell of Array.from(row.cells)) {
    const markdown = normalizeGridCellMarkdown(service.turndown(cell.innerHTML))
    const colSpan = Math.max(Number(cell.getAttribute("colspan") ?? 1) || 1, 1)
    cells.push(markdown)

    for (let i = 1; i < colSpan; i++) {
      cells.push([" "])
    }
  }

  return cells
}

function padRow(row: string[], columnCount: number): string[] {
  return row.length >= columnCount ? row : [...row, ...Array(columnCount - row.length).fill(" ")]
}

function formatTableRow(row: string[]): string {
  return `| ${row.join(" | ")} |`
}

function padGridRow(row: GridTableCell[], columnCount: number): GridTableCell[] {
  return row.length >= columnCount
    ? row
    : [...row, ...Array.from({ length: columnCount - row.length }, () => [" "])]
}

function getGridColumnWidths(rows: GridTableCell[][]): number[] {
  const columnCount = Math.max(...rows.map((row) => row.length))

  return Array.from({ length: columnCount }, (_unused, columnIndex) =>
    Math.max(3, ...rows.flatMap((row) => (row[columnIndex] ?? [" "]).map((line) => line.length)))
  )
}

function formatGridBorder(widths: number[], fill: "-" | "="): string {
  return `+${widths.map((width) => fill.repeat(width + 2)).join("+")}+`
}

function formatGridRow(row: GridTableCell[], widths: number[]): string[] {
  const rowHeight = Math.max(...row.map((cell) => cell.length))

  return Array.from({ length: rowHeight }, (_unused, lineIndex) => {
    const cells = row.map((cell, columnIndex) => {
      const line = cell[lineIndex] ?? ""
      return ` ${line.padEnd(widths[columnIndex])} `
    })

    return `|${cells.join("|")}|`
  })
}

function formatGridTable(rows: GridTableCell[][]): string {
  const columnCount = Math.max(...rows.map((row) => row.length))
  const paddedRows = rows.map((row) => padGridRow(row, columnCount))
  const widths = getGridColumnWidths(paddedRows)
  const border = formatGridBorder(widths, "-")
  const headerBorder = formatGridBorder(widths, "=")
  const [headerRow, ...bodyRows] = paddedRows
  const markdownRows = [border, ...formatGridRow(headerRow, widths), headerBorder]

  for (const row of bodyRows) {
    markdownRows.push(...formatGridRow(row, widths), border)
  }

  if (bodyRows.length === 0) {
    markdownRows.push(border)
  }

  return markdownRows.join("\n")
}

function addTiptapTableRule(service: TurndownService): void {
  service.addRule("tiptapTable", {
    filter: "table",
    replacement: (_content, node) => {
      const table = node as HTMLElement

      if (hasBlockRichTableCell(table)) {
        const gridRows = getTableRows(table)
          .map((row) => getGridRowCells(row, service))
          .filter((row) => row.length > 0)

        return gridRows.length === 0 ? "" : `\n\n${formatGridTable(gridRows)}\n\n`
      }

      const rows = getTableRows(table)
        .map((row) => getCompactRowCells(row, service))
        .filter((row) => row.length > 0)

      if (rows.length === 0) {
        return ""
      }

      const columnCount = Math.max(...rows.map((row) => row.length))
      const [headerRow, ...bodyRows] = rows.map((row) => padRow(row, columnCount))
      const separatorRow = Array(columnCount).fill("---")
      const markdownRows = [
        formatTableRow(headerRow),
        formatTableRow(separatorRow),
        ...bodyRows.map(formatTableRow),
      ]

      return `\n\n${markdownRows.join("\n")}\n\n`
    },
  })
}

export function createCanvasTurndownService(): TurndownService {
  const service = new TurndownService({
    headingStyle: "atx",
    codeBlockStyle: "fenced",
    bulletListMarker: "-",
  })

  addTiptapTableRule(service)

  return service
}
